// Scopify Pricing Configuration
// Hybrid pricing model with base price + per-user overages

export interface PricingTier {
  id: string;
  name: string;
  displayName: string;
  basePrice: number; // in cents
  includedUsers: number;
  overagePrice: number; // price per additional user in cents
  maxUsers?: number; // optional user limit
  features: string[];
  popular?: boolean;
}

export const PRICING_TIERS: Record<string, PricingTier> = {
  free: {
    id: 'free',
    name: 'Free',
    displayName: 'Free Trial',
    basePrice: 0,
    includedUsers: 3,
    overagePrice: 0,
    maxUsers: 3,
    features: [
      'Up to 3 users',
      '1 client company',
      'Custom templates only',
      '14-day trial',
      'Community support',
    ],
  },
  solo: {
    id: 'solo',
    name: 'Solo',
    displayName: 'Solo Consultant',
    basePrice: 29900, // $299
    includedUsers: 1,
    overagePrice: 0, // No overages - must upgrade
    maxUsers: 1,
    features: [
      '1 user',
      'Up to 5 active client companies',
      'Atlassian & Custom templates',
      'Email support',
      'Branded portal',
    ],
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    displayName: 'Starter',
    basePrice: 49900, // $499
    includedUsers: 10,
    overagePrice: 2500, // $25 per additional user
    features: [
      'Up to 10 users included',
      '$25/user for additional users',
      'Up to 50 active client companies (5x users)',
      'All template types',
      'Priority email support',
      'Custom branding',
      'API access',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    displayName: 'Premium',
    basePrice: 129900, // $1,299
    includedUsers: 25,
    overagePrice: 3500, // $35 per additional user
    popular: true,
    features: [
      'Up to 25 users included',
      '$35/user for additional users',
      'Up to 125 active client companies (5x users)',
      'All template types',
      'Priority phone & email support',
      'Advanced branding & customization',
      'API access',
      'Dedicated account manager',
      'Custom integrations',
    ],
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    displayName: 'Enterprise',
    basePrice: 499900, // $4,999
    includedUsers: 100,
    overagePrice: 4500, // $45 per additional user
    features: [
      'Up to 100 users included',
      '$45/user for additional users',
      'Up to 500 active client companies (5x users)',
      'All template types',
      '24/7 premium support',
      'White-label solution',
      'Custom domain (host on your own domain)',
      'API access',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantees',
      'On-premise deployment option',
      'Advanced security features',
    ],
  },
};

// Helper function to calculate total price for a tier with user count
export function calculateTierPrice(tierId: string, userCount: number): number {
  const tier = PRICING_TIERS[tierId];
  if (!tier) return 0;

  // For free tier or solo (no overages)
  if (tier.overagePrice === 0) {
    return tier.basePrice;
  }

  // Calculate overage users
  const overageUsers = Math.max(0, userCount - tier.includedUsers);
  return tier.basePrice + (overageUsers * tier.overagePrice);
}

// Helper to format price in dollars
export function formatPrice(priceInCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(priceInCents / 100);
}

// Helper to get recommended tier based on user count
export function getRecommendedTier(userCount: number): string {
  if (userCount === 1) return 'solo';
  if (userCount <= 10) return 'starter';
  if (userCount <= 25) return 'premium';
  return 'enterprise';
}

// Helper to check if user count is within tier limits
export function isWithinTierLimit(tierId: string, userCount: number): boolean {
  const tier = PRICING_TIERS[tierId];
  if (!tier) return false;
  
  // If tier has a max user limit (like free and solo), enforce it
  if (tier.maxUsers !== undefined) {
    return userCount <= tier.maxUsers;
  }
  
  // Otherwise, overages are allowed
  return true;
}

// Helper to get overage count
export function getOverageCount(tierId: string, userCount: number): number {
  const tier = PRICING_TIERS[tierId];
  if (!tier) return 0;
  
  return Math.max(0, userCount - tier.includedUsers);
}

// Helper to check if upgrade is needed
export function needsUpgrade(tierId: string, userCount: number): boolean {
  const tier = PRICING_TIERS[tierId];
  if (!tier) return false;
  
  // If tier has max users and we've exceeded it, upgrade needed
  if (tier.maxUsers !== undefined && userCount > tier.maxUsers) {
    return true;
  }
  
  return false;
}

// Get next tier recommendation
export function getNextTier(currentTierId: string): string | null {
  const tierOrder = ['free', 'solo', 'starter', 'premium', 'enterprise'];
  const currentIndex = tierOrder.indexOf(currentTierId);
  
  if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
    return null; // Already at highest tier
  }
  
  return tierOrder[currentIndex + 1];
}

// Calculate max active companies based on user count with configurable multiplier
export function calculateMaxActiveCompanies(
  tierId: string, 
  userCount: number, 
  multiplier: number = 5
): number {
  const tier = PRICING_TIERS[tierId];
  if (!tier) return 0;
  
  // Special case for free tier - fixed at 1 company
  if (tierId === 'free') return 1;
  
  // Calculate: multiplier × total number of users (base + overages)
  return userCount * multiplier;
}

// Helper to display company limit in UI
export function getCompanyLimitDisplay(
  tierId: string, 
  userCount: number, 
  multiplier: number = 5
): string {
  const limit = calculateMaxActiveCompanies(tierId, userCount, multiplier);
  
  if (tierId === 'free') return '1 client company';
  
  return `Up to ${limit} active client companies`;
}

// ============================================================================
// LICENSE MANAGEMENT ADD-ONS
// ============================================================================

export interface LicenseManagementAddon {
  id: string;
  name: string;
  displayName: string;
  price: number; // monthly price in cents
  description: string;
  features: string[];
  maxLicenses: number | 'unlimited';
  tier: 'basic' | 'pro' | 'enterprise';
  requiredBasePlan?: string[]; // Which base plans can add this
}

export const LICENSE_MANAGEMENT_ADDONS: Record<string, LicenseManagementAddon> = {
  license_basic: {
    id: 'license_basic',
    name: 'License Management Basic',
    displayName: 'License Tracker',
    price: 4900, // $49/month
    description: 'Essential license tracking and expiration reminders',
    tier: 'basic',
    maxLicenses: 50,
    requiredBasePlan: ['solo', 'starter', 'premium', 'enterprise'],
    features: [
      'Track up to 50 client licenses',
      'Expiration date tracking',
      'Automated email reminders (90/60/30 days)',
      'Dashboard with upcoming expirations',
      'Search and filter licenses',
      'Export to CSV',
      'License portfolio value tracking',
      'Contact management',
    ],
  },
  license_pro: {
    id: 'license_pro',
    name: 'License Management Pro',
    displayName: 'License Pipeline',
    price: 14900, // $149/month
    description: 'Complete renewal pipeline with CRM-style tracking',
    tier: 'pro',
    maxLicenses: 200,
    requiredBasePlan: ['starter', 'premium', 'enterprise'],
    features: [
      'Everything in Basic, plus:',
      'Track up to 200 client licenses',
      'Renewal workflow pipeline (Kanban view)',
      'Activity timeline and notes',
      'Renewal probability scoring',
      'Email templates for renewals',
      'Task management and reminders',
      'Pipeline analytics and forecasting',
      'Revenue forecasting by probability',
      'BDM performance tracking',
    ],
  },
  license_enterprise: {
    id: 'license_enterprise',
    name: 'License Management Enterprise',
    displayName: 'Full Procurement',
    price: 34900, // $349/month
    description: 'Comprehensive license procurement and compliance management',
    tier: 'enterprise',
    maxLicenses: 'unlimited',
    requiredBasePlan: ['premium', 'enterprise'],
    features: [
      'Everything in Pro, plus:',
      'Unlimited licenses',
      'Purchase order tracking',
      'Invoice management',
      'Cost analysis & budgeting',
      'ROI tracking',
      'License utilization monitoring',
      'Right-sizing recommendations',
      'Compliance monitoring & audit support',
      'Vendor management',
      'Document repository (contracts, quotes, invoices)',
      'Multi-year planning & forecasting',
      'Client self-service portal',
      'Vendor API integrations (Atlassian, ServiceNow, etc.)',
      'Calendar integration',
      'Advanced analytics & custom reports',
      'Client health scores',
    ],
  },
};

// Helper to check if firm has license management access
export function hasLicenseManagementAccess(licenseAddonId: string | null): boolean {
  return licenseAddonId !== null && LICENSE_MANAGEMENT_ADDONS[licenseAddonId] !== undefined;
}

// Helper to get license management tier level
export function getLicenseManagementTier(licenseAddonId: string | null): 'none' | 'basic' | 'pro' | 'enterprise' {
  if (!licenseAddonId || !LICENSE_MANAGEMENT_ADDONS[licenseAddonId]) {
    return 'none';
  }
  return LICENSE_MANAGEMENT_ADDONS[licenseAddonId].tier;
}

// Helper to check if specific feature is available
export function hasLicenseFeature(
  licenseAddonId: string | null,
  feature: 'basic' | 'pipeline' | 'compliance' | 'analytics' | 'client_portal' | 'unlimited'
): boolean {
  const tier = getLicenseManagementTier(licenseAddonId);
  
  if (tier === 'none') return false;
  
  switch (feature) {
    case 'basic':
      return ['basic', 'pro', 'enterprise'].includes(tier);
    case 'pipeline':
      return ['pro', 'enterprise'].includes(tier);
    case 'compliance':
    case 'analytics':
    case 'client_portal':
      return tier === 'enterprise';
    case 'unlimited':
      return tier === 'enterprise';
    default:
      return false;
  }
}

// Helper to get max licenses for a tier
export function getMaxLicenses(licenseAddonId: string | null): number {
  if (!licenseAddonId || !LICENSE_MANAGEMENT_ADDONS[licenseAddonId]) {
    return 0;
  }
  const addon = LICENSE_MANAGEMENT_ADDONS[licenseAddonId];
  return addon.maxLicenses === 'unlimited' ? Infinity : addon.maxLicenses;
}

// Helper to check if license count is within limit
export function isWithinLicenseLimit(licenseAddonId: string | null, licenseCount: number): boolean {
  const maxLicenses = getMaxLicenses(licenseAddonId);
  return licenseCount <= maxLicenses;
}

// Helper to get next license management tier
export function getNextLicenseTier(currentAddonId: string | null): string | null {
  const tierOrder = ['license_basic', 'license_pro', 'license_enterprise'];
  
  if (!currentAddonId) {
    return 'license_basic'; // First tier if not subscribed
  }
  
  const currentIndex = tierOrder.indexOf(currentAddonId);
  
  if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
    return null; // Already at highest tier or invalid
  }
  
  return tierOrder[currentIndex + 1];
}

// Helper to check if base plan can add license management
export function canAddLicenseManagement(basePlanId: string, licenseAddonId: string): boolean {
  const addon = LICENSE_MANAGEMENT_ADDONS[licenseAddonId];
  if (!addon) return false;
  
  if (!addon.requiredBasePlan) return true;
  
  return addon.requiredBasePlan.includes(basePlanId);
}
